/**
 * Capability-based analysis labels and tooltips
 * 
 * These labels abstract away the underlying model/vendor names to provide
 * stable capability contracts. Models can be swapped without UI changes.
 */

export const ANALYSIS_TYPES = {
  REGULATORY_COMPLIANCE: 'regulatory',
  GENERAL_SAFETY: 'safety',
  USER_PERCEPTION: 'perception',
} as const;

export type AnalysisType = typeof ANALYSIS_TYPES[keyof typeof ANALYSIS_TYPES];

export interface AnalysisLabelConfig {
  shortName: string;
  fullName: string;
  tooltip: string;
  prefix: string;  // For category prefixes in heatmap/charts
}

export const ANALYSIS_LABELS: Record<AnalysisType, AnalysisLabelConfig> = {
  [ANALYSIS_TYPES.REGULATORY_COMPLIANCE]: {
    shortName: 'Regulatory Compliance',
    fullName: 'Standards & Regulatory Compliance Analysis',
    tooltip: 'Focused on compliance-oriented evaluation aligned to frameworks like ISO/IEC (e.g., 27001-style controls thinking), the EU AI Act, and the NIST AI Risk Management Framework (AI RMF). Flags gaps in governance, documentation, risk controls, human oversight, transparency, and audit readiness.',
    prefix: 'Compliance',
  },
  [ANALYSIS_TYPES.GENERAL_SAFETY]: {
    shortName: 'General Safety',
    fullName: 'General Safety & Policy Monitoring',
    tooltip: 'General monitoring for unsafe or disallowed content, policy violations, unsafe statements, and other high-risk interaction patterns. Used as a broad safety gate before/after generation and for ongoing monitoring.',
    prefix: 'Safety',
  },
  [ANALYSIS_TYPES.USER_PERCEPTION]: {
    shortName: 'User Perception',
    fullName: 'User Perception & Experience Analysis',
    tooltip: 'Evaluates how users will perceive the content: tone, clarity, trust impact, hallucinations, and likelihood of confusion or misuse.',
    prefix: 'Perception',
  },
};

// Mapping from old names to new analysis types (for backward compatibility)
export const LEGACY_NAME_MAPPING: Record<string, AnalysisType> = {
  // Granite Guardian → Regulatory Compliance
  'granite': ANALYSIS_TYPES.REGULATORY_COMPLIANCE,
  'granite guardian': ANALYSIS_TYPES.REGULATORY_COMPLIANCE,
  'granite-guardian': ANALYSIS_TYPES.REGULATORY_COMPLIANCE,
  'guardian': ANALYSIS_TYPES.REGULATORY_COMPLIANCE,
  
  // Llama Guard → General Safety
  'llama guard': ANALYSIS_TYPES.GENERAL_SAFETY,
  'llama-guard': ANALYSIS_TYPES.GENERAL_SAFETY,
  'llamaguard': ANALYSIS_TYPES.GENERAL_SAFETY,
  'harm': ANALYSIS_TYPES.GENERAL_SAFETY,
  
  // Semantic/Llama-3 → User Perception
  'semantic': ANALYSIS_TYPES.USER_PERCEPTION,
  'llm': ANALYSIS_TYPES.USER_PERCEPTION,
  'embedding': ANALYSIS_TYPES.USER_PERCEPTION,
  'perception': ANALYSIS_TYPES.USER_PERCEPTION,
};

/**
 * Get the analysis type from a legacy/internal name
 */
export function getAnalysisType(name: string): AnalysisType | undefined {
  const normalized = name.toLowerCase().trim();
  return LEGACY_NAME_MAPPING[normalized];
}

/**
 * Get analysis label config from a legacy/internal name
 */
export function getAnalysisLabel(name: string): AnalysisLabelConfig | undefined {
  const type = getAnalysisType(name);
  return type ? ANALYSIS_LABELS[type] : undefined;
}

/**
 * Category name mappings for heatmap/charts display
 * Maps internal category names to user-friendly display names with analysis group
 */
export const CATEGORY_DISPLAY_NAMES: Record<string, { display: string; analysisType: AnalysisType }> = {
  // General Safety (formerly Llama Guard) categories
  'Violent Crimes': { display: 'Violent Crimes', analysisType: ANALYSIS_TYPES.GENERAL_SAFETY },
  'Non-Violent Crimes': { display: 'Non-Violent Crimes', analysisType: ANALYSIS_TYPES.GENERAL_SAFETY },
  'Sex-Related Crimes': { display: 'Sex-Related Crimes', analysisType: ANALYSIS_TYPES.GENERAL_SAFETY },
  'Child Sexual Exploitation': { display: 'Child Sexual Exploitation', analysisType: ANALYSIS_TYPES.GENERAL_SAFETY },
  'Defamation': { display: 'Defamation', analysisType: ANALYSIS_TYPES.GENERAL_SAFETY },
  'Specialized Advice': { display: 'Specialized Advice', analysisType: ANALYSIS_TYPES.GENERAL_SAFETY },
  'Privacy': { display: 'Privacy', analysisType: ANALYSIS_TYPES.GENERAL_SAFETY },
  'Intellectual Property': { display: 'Intellectual Property', analysisType: ANALYSIS_TYPES.GENERAL_SAFETY },
  'Indiscriminate Weapons': { display: 'Indiscriminate Weapons', analysisType: ANALYSIS_TYPES.GENERAL_SAFETY },
  'Hate': { display: 'Hate', analysisType: ANALYSIS_TYPES.GENERAL_SAFETY },
  'Suicide & Self-Harm': { display: 'Suicide & Self-Harm', analysisType: ANALYSIS_TYPES.GENERAL_SAFETY },
  'Sexual Content': { display: 'Sexual Content', analysisType: ANALYSIS_TYPES.GENERAL_SAFETY },
  'Elections': { display: 'Elections', analysisType: ANALYSIS_TYPES.GENERAL_SAFETY },
  'Code Interpreter Abuse': { display: 'Code Interpreter Abuse', analysisType: ANALYSIS_TYPES.GENERAL_SAFETY },
  
  // Regulatory Compliance (formerly Granite Guardian) - Risk Detection
  'Compliance: Harm': { display: 'Harm Detection', analysisType: ANALYSIS_TYPES.REGULATORY_COMPLIANCE },
  'Compliance: Social Bias': { display: 'Social Bias', analysisType: ANALYSIS_TYPES.REGULATORY_COMPLIANCE },
  'Compliance: Jailbreak': { display: 'Jailbreak Detection', analysisType: ANALYSIS_TYPES.REGULATORY_COMPLIANCE },
  'Compliance: Violence': { display: 'Violence Detection', analysisType: ANALYSIS_TYPES.REGULATORY_COMPLIANCE },
  'Compliance: Profanity': { display: 'Profanity Detection', analysisType: ANALYSIS_TYPES.REGULATORY_COMPLIANCE },
  'Compliance: Sexual Content': { display: 'Sexual Content', analysisType: ANALYSIS_TYPES.REGULATORY_COMPLIANCE },
  'Compliance: Unethical Behavior': { display: 'Unethical Behavior', analysisType: ANALYSIS_TYPES.REGULATORY_COMPLIANCE },
  // Regulatory Compliance - RAG Quality
  'Compliance: Relevance': { display: 'Context Relevance', analysisType: ANALYSIS_TYPES.REGULATORY_COMPLIANCE },
  'Compliance: Groundedness': { display: 'Groundedness', analysisType: ANALYSIS_TYPES.REGULATORY_COMPLIANCE },
  'Compliance: Answer Relevance': { display: 'Answer Relevance', analysisType: ANALYSIS_TYPES.REGULATORY_COMPLIANCE },
  
  // User Perception (formerly Semantic) - Embedding-based
  'Perception: Groundedness': { display: 'Groundedness', analysisType: ANALYSIS_TYPES.USER_PERCEPTION },
  'Perception: Hallucination': { display: 'Hallucination Check', analysisType: ANALYSIS_TYPES.USER_PERCEPTION },
  'Perception: Tone': { display: 'Appropriate Tone', analysisType: ANALYSIS_TYPES.USER_PERCEPTION },
  'Perception: Bias': { display: 'Bias Detection', analysisType: ANALYSIS_TYPES.USER_PERCEPTION },
  // User Perception - LLM-based
  'Perception: LLM Groundedness': { display: 'LLM Groundedness', analysisType: ANALYSIS_TYPES.USER_PERCEPTION },
  'Perception: LLM Hallucination': { display: 'LLM Hallucination Check', analysisType: ANALYSIS_TYPES.USER_PERCEPTION },
  'Perception: LLM Tone': { display: 'LLM Tone Analysis', analysisType: ANALYSIS_TYPES.USER_PERCEPTION },
};

/**
 * Get sorted categories grouped by analysis type for display
 * Order: General Safety → Regulatory Compliance → User Perception, then alphabetically within each group
 */
export function getSortedCategories(categories: string[]): string[] {
  const typeOrder: AnalysisType[] = [
    ANALYSIS_TYPES.GENERAL_SAFETY,
    ANALYSIS_TYPES.REGULATORY_COMPLIANCE,
    ANALYSIS_TYPES.USER_PERCEPTION,
  ];
  
  return [...categories].sort((a, b) => {
    const aInfo = CATEGORY_DISPLAY_NAMES[a];
    const bInfo = CATEGORY_DISPLAY_NAMES[b];
    
    // Get type indices (unknown types go to the end)
    const aTypeIndex = aInfo ? typeOrder.indexOf(aInfo.analysisType) : 999;
    const bTypeIndex = bInfo ? typeOrder.indexOf(bInfo.analysisType) : 999;
    
    // First sort by type
    if (aTypeIndex !== bTypeIndex) {
      return aTypeIndex - bTypeIndex;
    }
    
    // Then alphabetically within the same type
    return a.localeCompare(b);
  });
}

/**
 * Get the display name for a category
 */
export function getCategoryDisplayName(internalName: string): string {
  return CATEGORY_DISPLAY_NAMES[internalName]?.display || internalName;
}

/**
 * Get the analysis group header for a category
 */
export function getCategoryAnalysisGroup(internalName: string): string {
  const info = CATEGORY_DISPLAY_NAMES[internalName];
  if (!info) return 'Other';
  return ANALYSIS_LABELS[info.analysisType].shortName;
}



